import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { askGemini } from '../../config/gemini';
import Sidebar from '../Shared/Sidebar';
import { v4 as uuidv4 } from 'uuid';

export default function AIReportWriter() {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [generatedReport, setGeneratedReport] = useState('');

  // AI models often return markdown (**bold**, # headings) which showed up
  // as literal *** in saved reports. Strip it so reports are clean text.
  function stripMarkdown(text) {
    if (!text) return '';
    return text
      .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '• ')
      .replace(/`{1,3}/g, '')
      .replace(/^---+$/gm, '')
      .trim();
  }
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    reportType: 'Progress Report',
    relatedTo: 'student',
    personId: '',
    observations: '',
    academicProgress: '',
    behaviourProgress: '',
    healthProgress: '',
    communityProgress: '',
    lifeSkillsProgress: '',
    keyEvents: '',
    recommendations: '',
    followUpNeeded: false,
    followUpDate: '',
  });

  useEffect(() => {
    fetchPeople();
  }, []);

  async function fetchPeople() {
    try {
      const studentsSnap = await getDocs(
        collection(db, 'students')
      );
      const parentsSnap = await getDocs(
        collection(db, 'parents')
      );
      setStudents(
        studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setParents(
        parentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    const value = e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  }

  // Get selected person details
  function getSelectedPerson() {
    if (form.relatedTo === 'student') {
      return students.find(s => s.id === form.personId);
    }
    return parents.find(p => p.id === form.personId);
  }

  async function handleGenerateReport() {
    if (!form.personId) {
      setError('Please select a person first!');
      return;
    }
    if (!form.observations) {
      setError('Please add your observations first!');
      return;
    }

    setGenerating(true);
    setError('');

    const person = getSelectedPerson();
    const personName = person
      ? `${person.firstName} ${person.lastName}`
      : 'Unknown';

    const prompt = `
You are a professional NGO report writer for Iyanna Foundation, a Tanzania-based NGO that mentors children below Class 4 and supports their families.

Write a formal, professional ${form.reportType} for the following:

PERSON: ${personName}
TYPE: ${form.relatedTo === 'student' ? 'Student' : 'Parent'}
${form.relatedTo === 'student' ? `CLASS: ${person?.class || 'Unknown'}
SCHOOL: ${person?.school || 'Unknown'}` : `BUSINESS: ${person?.businessType || 'Unknown'}`}

STAFF OBSERVATIONS:
${form.observations}

${form.relatedTo === 'student' ? `
PROGRESS ASSESSMENT:
- Academic Performance: ${form.academicProgress || 'Not assessed'}
- Behaviour at School: ${form.behaviourProgress || 'Not assessed'}
- Health & Wellbeing: ${form.healthProgress || 'Not assessed'}
- Community Behaviour: ${form.communityProgress || 'Not assessed'}
- Chess & Life Skills: ${form.lifeSkillsProgress || 'Not assessed'}
` : ''}

KEY EVENTS OR ACTIVITIES:
${form.keyEvents || 'None specified'}

STAFF RECOMMENDATIONS:
${form.recommendations || 'None specified'}

Please write a formal professional report with:
1. A clear introduction about the person
2. Detailed observations and findings
3. Progress assessment (if student)
4. Recommendations and next steps
5. A professional conclusion

The report should be formal, empathetic, detailed and suitable for organizational records and donor viewing. Use professional NGO language. Write in English. The report should be at least 300 words. IMPORTANT: Write in plain text only — do NOT use any markdown formatting such as asterisks (**bold**), hashes (# headings), bullet symbols, or underscores. Use plain paragraph text and numbered sections written as normal sentences.
`;

    try {
      const result = await askGemini(prompt);
      setGeneratedReport(stripMarkdown(result));
      setStep(2);
    } catch (err) {
      setError('AI is not available right now. Please try again.');
      console.error(err);
    }
    setGenerating(false);
  }

  async function handleSaveReport() {
    setLoading(true);
    try {
      const reportId = `IYA-RPT-${uuidv4().slice(0, 8).toUpperCase()}`;
      const person = getSelectedPerson();

      await addDoc(collection(db, 'reports'), {
        reportId,
        title: `${form.reportType} — ${person?.firstName} ${person?.lastName}`,
        reportType: form.reportType,
        relatedTo: form.relatedTo,
        personId: form.personId,
        description: generatedReport,
        academicProgress: form.academicProgress,
        behaviourProgress: form.behaviourProgress,
        healthProgress: form.healthProgress,
        communityProgress: form.communityProgress,
        lifeSkillsProgress: form.lifeSkillsProgress,
        recommendations: form.recommendations,
        followUpNeeded: form.followUpNeeded,
        followUpDate: form.followUpDate,
        aiGenerated: true,
        staffObservations: form.observations,
        submittedBy: currentUser.uid,
        status: 'Submitted',
        photoURLs: [],
        documentURLs: [],
        createdAt: serverTimestamp(),
      });

      setSuccess('Report saved successfully!');
      setStep(1);
      setGeneratedReport('');
      setForm({
        reportType: 'Progress Report',
        relatedTo: 'student',
        personId: '',
        observations: '',
        academicProgress: '',
        behaviourProgress: '',
        healthProgress: '',
        communityProgress: '',
        lifeSkillsProgress: '',
        keyEvents: '',
        recommendations: '',
        followUpNeeded: false,
        followUpDate: '',
      });
    } catch (err) {
      setError('Could not save report. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  const progressOptions = [
    '', 'Excellent', 'Good', 'Average', 'Needs Improvement'
  ];

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              AI Report Writer
            </div>
            <div style={styles.pageSub}>
              Give basic information and AI will write
              a professional report for you
            </div>
          </div>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        {/* STEP INDICATOR */}
        <div style={styles.stepRow}>
          <div style={{
            ...styles.stepItem,
            ...(step === 1 ? styles.stepActive : {})
          }}>
            <div style={{
              ...styles.stepCircle,
              background: step >= 1
                ? 'linear-gradient(135deg, #14452F, #1F6B45)'
                : '#E5E7EB'
            }}>
              1
            </div>
            <span style={styles.stepLabel}>
              Your Observations
            </span>
          </div>
          <div style={styles.stepLine} />
          <div style={{
            ...styles.stepItem,
            ...(step === 2 ? styles.stepActive : {})
          }}>
            <div style={{
              ...styles.stepCircle,
              background: step >= 2
                ? 'linear-gradient(135deg, #F57C00, #FFA726)'
                : '#E5E7EB'
            }}>
              2
            </div>
            <span style={styles.stepLabel}>
              AI Generated Report
            </span>
          </div>
        </div>

        {/* STEP 1 — INPUT */}
        {step === 1 && (
          <div>
            <div style={styles.panel}>
              <div style={styles.sectionTitle}>
                Report Information
              </div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Report Type *
                  </label>
                  <select
                    style={styles.formInput}
                    name="reportType"
                    value={form.reportType}
                    onChange={handleChange}
                  >
                    <option>Progress Report</option>
                    <option>Session Report</option>
                    <option>Home Visit Report</option>
                    <option>School Visit Report</option>
                    <option>Incident Report</option>
                    <option>Monthly Report</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Related To *
                  </label>
                  <select
                    style={styles.formInput}
                    name="relatedTo"
                    value={form.relatedTo}
                    onChange={handleChange}
                  >
                    <option value="student">Student</option>
                    <option value="parent">Parent</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Select{' '}
                    {form.relatedTo === 'student'
                      ? 'Student'
                      : 'Parent'} *
                  </label>
                  <select
                    style={styles.formInput}
                    name="personId"
                    value={form.personId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select...</option>
                    {form.relatedTo === 'student'
                      ? students.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.firstName} {s.lastName} —{' '}
                          {s.studentId}
                        </option>
                      ))
                      : parents.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.firstName} {p.lastName} —{' '}
                          {p.parentId}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.panel}>
              <div style={styles.sectionTitle}>
                Your Observations
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  What did you observe? *
                  (Write in simple words — AI will make it professional)
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '120px',
                    resize: 'vertical'
                  }}
                  name="observations"
                  value={form.observations}
                  onChange={handleChange}
                  placeholder="e.g. The student has been coming to school regularly. He seems more focused in class. His teacher said he is improving in maths. He participates well in Saturday sessions. His mother says he is helping more at home..."
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Key Events or Activities
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '80px',
                    resize: 'vertical'
                  }}
                  name="keyEvents"
                  value={form.keyEvents}
                  onChange={handleChange}
                  placeholder="e.g. Attended 4 Saturday sessions, participated in chess tournament, received new uniform..."
                />
              </div>
            </div>

            {form.relatedTo === 'student' && (
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>
                  Progress Assessment
                </div>
                <div style={styles.formGrid}>
                  {[
                    { name: 'academicProgress', label: 'Academic' },
                    { name: 'behaviourProgress', label: 'Behaviour' },
                    { name: 'healthProgress', label: 'Health' },
                    { name: 'communityProgress', label: '🏘️ Community' },
                    { name: 'lifeSkillsProgress', label: 'Life Skills' },
                  ].map(p => (
                    <div key={p.name} style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        {p.label}
                      </label>
                      <select
                        style={styles.formInput}
                        name={p.name}
                        value={form[p.name]}
                        onChange={handleChange}
                      >
                        {progressOptions.map(o => (
                          <option key={o} value={o}>
                            {o || 'Select rating'}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.panel}>
              <div style={styles.sectionTitle}>
                Recommendations
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Your Recommendations
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '80px',
                    resize: 'vertical'
                  }}
                  name="recommendations"
                  value={form.recommendations}
                  onChange={handleChange}
                  placeholder="e.g. Continue monitoring, provide extra support in reading, schedule home visit..."
                />
              </div>
              <div style={styles.checkRow}>
                <input
                  type="checkbox"
                  name="followUpNeeded"
                  checked={form.followUpNeeded}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <label style={styles.formLabel}>
                  Follow Up Needed
                </label>
              </div>
              {form.followUpNeeded && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Follow Up Date
                  </label>
                  <input
                    style={styles.formInput}
                    type="date"
                    name="followUpDate"
                    value={form.followUpDate}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>

            <button
              style={styles.generateBtn}
              onClick={handleGenerateReport}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span style={styles.spinner}>⟳</span>
                  AI is writing your report...
                </>
              ) : (
                'Generate Professional Report with AI'
              )}
            </button>
          </div>
        )}

        {/* STEP 2 — GENERATED REPORT */}
        {step === 2 && (
          <div>
            <div style={styles.aiReportCard}>
              <div style={styles.aiReportHeader}>
                <div style={styles.aiReportTitle}>
                  AI Generated Report
                </div>
                <div style={styles.aiReportSub}>
                  Review and edit if needed before saving
                </div>
              </div>
              <textarea
                style={styles.reportTextarea}
                value={generatedReport}
                onChange={e => setGeneratedReport(e.target.value)}
              />
            </div>

            <div style={styles.btnRow}>
              <button
                style={styles.backBtn}
                onClick={() => setStep(1)}
              >
                ← Edit Info
              </button>
              <button
                style={styles.regenerateBtn}
                onClick={handleGenerateReport}
                disabled={generating}
              >
                {generating ? '⟳ Regenerating...' : 'Regenerate'}
              </button>
              <button
                style={styles.saveBtn}
                onClick={handleSaveReport}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Report'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '24px',
    background: '#fff',
    borderRadius: '14px',
    padding: '16px 20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepActive: { opacity: 1 },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
  },
  stepLine: {
    flex: 1,
    height: '2px',
    background: '#E5E7EB',
    margin: '0 16px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '8px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    accentColor: '#14452F',
  },
  generateBtn: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(20,69,47,0.3)',
    marginBottom: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  spinner: {
    fontSize: '18px',
    animation: 'spin 1s linear infinite',
  },
  aiReportCard: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '16px',
  },
  aiReportHeader: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    padding: '16px 20px',
  },
  aiReportTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
  },
  aiReportSub: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.7)',
    marginTop: '3px',
  },
  reportTextarea: {
    width: '100%',
    minHeight: '400px',
    padding: '20px',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#374151',
    lineHeight: 1.7,
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  btnRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
  },
  backBtn: {
    padding: '13px 18px',
    background: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  regenerateBtn: {
    padding: '13px 18px',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(245,124,0,0.25)',
  },
  saveBtn: {
    flex: 1,
    padding: '13px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(20,69,47,0.25)',
  },
};